/*
 * SPIRE infrastructure — Identity sub-surface (/identity/spire-health).
 *
 * The home for the SPIFFE/SPIRE infrastructure detail that the Identity landing scope cards
 * summarize: SPIRE server topology and attestation, trust domains, root CA and trust bundle,
 * rotation defaults, and federation. Live SVID lifecycle counts come from GET /api/v1/identity/fleet;
 * the configuration is read live from GET /api/v1/identity/organization (the same source the
 * Organization detail page edits), with click-throughs to the authoritative editors — no duplicate
 * backend. Representative values render only as a fallback when a field isn't yet populated.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ServerCog, Globe, Key, RefreshCw, Share2, ArrowUpRight,
  ShieldCheck, AlertTriangle,
} from 'lucide-react';
import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { MetricCard } from '../components/v2/MetricCard';
import { getIdentityFleet, getIdentityOrganization } from '../services/api';

const FLEET_CARDS = [
  { key: '__total__',         label: 'Total agents',      tone: 'action' },
  { key: 'IDENTITY_ASSIGNED', label: 'Identity assigned', tone: 'warning' },
  { key: 'ACTIVE',            label: 'Active',            tone: 'trust' },
  { key: 'VERIFIED',          label: 'Verified',          tone: 'success' },
  { key: 'REVOKED',           label: 'Revoked',           tone: 'danger' },
  { key: 'EXPIRED',           label: 'Expired',           tone: 'muted' },
];

/* Fallback rows — shown only when the live organization config hasn't populated a section yet. */
const FALLBACK = {
  spire: [['Server topology', 'Not configured'], ['Node attestation', '—'], ['Workload attestation', '—']],
  trustDomains: [['Primary', 'Not configured']],
  rootCa: [['Root CA', 'Not configured']],
  rotation: [['Default SVID TTL', '—'], ['Rotation window', '—']],
  federation: [['Active partners', '0']],
};

const arr = (v) => (Array.isArray(v) ? v : []);

/* Derive the read-only infrastructure section rows from the live organization payload. */
function sectionsFrom(org) {
  const spire = org?.spireBootstrap || {};
  const trustDomains = arr(org?.trustDomains);
  const rotation = arr(org?.rotationDefaults);
  const federations = arr(org?.federations);
  const rootCas = arr(org?.rootCas);

  const spireRows = spire.topology
    ? [
        ['Server topology', spire.topology],
        ['Node attestation', spire.nodeAttestationNote || arr(spire.nodeAttestation).join(', ') || '—'],
        ['Workload attestation', spire.workloadAttestationNote || arr(spire.workloadAttestation).join(', ') || '—'],
      ]
    : FALLBACK.spire;

  const trustRows = trustDomains.length
    ? trustDomains.slice(0, 3).map((td) => [td.role || 'Domain', td.spiffeId || '—'])
    : FALLBACK.trustDomains;

  const rootRows = rootCas.length
    ? [['Root CA', rootCas[0].name || '—'], ...arr(rootCas[0].facts).map((f) => [f.label, f.value])].slice(0, 4)
    : FALLBACK.rootCa;

  const rotationRows = rotation.length
    ? rotation.slice(0, 4).map((r) => [r.label, r.value])
    : FALLBACK.rotation;

  const fedRows = federations.length
    ? [['Active partners', String(federations.length)],
       ...federations.slice(0, 2).map((f) => [f.name || 'Peer', f.direction || f.bundle || '—'])]
    : FALLBACK.federation;

  return [
    { id: 'spire',         icon: ServerCog, title: 'SPIRE bootstrap',        rows: spireRows,    cta: { label: 'Open Organization', path: '/identity/organization' } },
    { id: 'trust-domains', icon: Globe,     title: 'Trust domains',          rows: trustRows,    cta: { label: 'Open Organization', path: '/identity/organization' } },
    { id: 'root-ca',       icon: Key,       title: 'Root CA & trust bundle', rows: rootRows,     cta: { label: 'Open Organization', path: '/identity/organization' } },
    { id: 'rotation',      icon: RefreshCw, title: 'Rotation defaults',      rows: rotationRows, cta: { label: 'Open Organization', path: '/identity/organization' } },
    { id: 'federation',    icon: Share2,    title: 'Federation',             rows: fedRows,      cta: { label: 'Federation map',    path: '/identity/federation-map' } },
  ];
}

export default function IdentitySpireInfraPageV2() {
  const navigate = useNavigate();
  const [fleet, setFleet] = useState(null);
  const [org, setOrg] = useState(null);

  useEffect(() => {
    let alive = true;
    getIdentityFleet()
      .then((res) => { if (alive) setFleet((res && res.data) ? res.data : { mode: 'preview', total: 0, byState: {} }); })
      .catch(() => { if (alive) setFleet({ mode: 'preview', total: 0, byState: {} }); });
    getIdentityOrganization()
      .then((res) => { if (alive && res?.ok) setOrg(res.data || {}); })
      .catch(() => { /* config is best-effort; fallback rows render */ });
    return () => { alive = false; };
  }, []);

  const mode = fleet?.mode || 'preview';
  const byState = fleet?.byState || {};
  const cardValue = (key) => key === '__total__' ? (fleet?.total ?? 0) : (byState[key] ?? 0);
  const sections = useMemo(() => sectionsFrom(org), [org]);

  return (
    <V2PageShell
      title="SPIRE infrastructure"
      subtitle="SPIRE topology, trust domains, root CA, rotation, and federation for the identity plane."
      tags={
        <>
          <Pill tone="trust" size="sm" dot>SPIFFE plane</Pill>
          <Pill tone={mode === 'wired' ? 'success' : 'warning'} size="sm">
            {mode === 'wired' ? 'Live' : 'Preview'}
          </Pill>
        </>
      }
      actions={
        <Button variant="ghost" size="sm" iconLeft={<ArrowLeft size={14} />} onClick={() => navigate('/identity')}>
          Back to Identity
        </Button>
      }
    >
      {/* Honesty banner — preview (NoOp SPIRE) vs wired (real SVIDs) */}
      <div
        className={
          'flex items-start gap-2.5 rounded-v2-md px-3.5 py-2.5 mb-4 ' +
          (mode === 'wired' ? 'bg-v2-action-soft' : 'bg-v2-warning-soft')
        }
      >
        {mode === 'wired'
          ? <ShieldCheck size={15} className="text-v2-action mt-0.5 flex-shrink-0" aria-hidden="true" />
          : <AlertTriangle size={15} className="text-v2-warning mt-0.5 flex-shrink-0" aria-hidden="true" />}
        <div className="text-[12px] text-v2-ink leading-relaxed">
          {mode === 'wired' ? (
            <><span className="font-semibold">SPIRE wired.</span> Real X.509-SVIDs are minted and verified at the gateway edge. Counts below are live.</>
          ) : (
            <><span className="font-semibold">Preview · BYO-SPIRE.</span> SPIRE Workload API not wired — 0 SVIDs minted. Counts below reflect real lifecycle state, not demo data.</>
          )}
        </div>
      </div>

      {/* Live SVID fleet counts */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-7">
        {FLEET_CARDS.map((c) => (
          <MetricCard key={c.key} label={c.label} value={String(cardValue(c.key))} tone={c.tone} />
        ))}
      </div>

      {/* Infrastructure configuration — read live from the organization scope */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sections.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.id} padding="md">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-md bg-v2-muted text-v2-ink-secondary flex items-center justify-center flex-shrink-0">
                    <Icon size={16} aria-hidden="true" />
                  </div>
                  <div className="text-[14px] font-semibold text-v2-ink">{s.title}</div>
                </div>
                <Button variant="ghost" size="sm" iconRight={<ArrowUpRight size={13} />} onClick={() => navigate(s.cta.path)}>
                  {s.cta.label}
                </Button>
              </div>
              <div className="mt-3 flex flex-col gap-2">
                {s.rows.map(([label, value], i) => (
                  <div key={label + i} className="flex items-baseline justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-wide text-v2-ink-muted font-semibold">{label}</span>
                    <span className="text-[12.5px] text-v2-ink text-right min-w-0 truncate">{value}</span>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </V2PageShell>
  );
}
